/**
 * VerificationStrip — iter 141
 *
 * A small trust-layer strip that sits between the hero and the live-jobs
 * ticker. Three icons + short plain-English lines so a first-time visitor
 * understands what "verified" actually means on Simonara before scrolling
 * further.
 *
 * Copy is intentionally factual — mirrors the iter-136 talent-region gate
 * requirements (phone, work-auth, address). No unverified claims, no
 * statistics, no competitor references.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, MapPin } from "lucide-react";

const ITEMS = [
    {
        Icon: ShieldCheck,
        title: "Phone-verified · Work-authorization attested",
        body: "This talent verified a US mobile number and has attested they are authorized to work in the US. Simonara does not independently verify work authorization.",
        testId: "verify-strip-tier0",
    },
    {
        Icon: MapPin,
        title: "State + ZIP on file",
        body: "Talent self-reports their state and ZIP at signup. Simonara does not verify street addresses.",
        testId: "verify-strip-address",
    },
];

const VerificationStrip = () => (
    <section
        className="border-y border-border/60 bg-muted/30 py-10 sm:py-12"
        data-testid="home-verification-strip"
    >
        <div className="container-page">
            <p className="text-center text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                How verification works
            </p>
            <h2 className="mt-2 text-center font-heading font-semibold text-2xl sm:text-3xl text-foreground">
                Real people. Phone-verified at signup, ID-verifiable on request.
            </h2>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {ITEMS.map(({ Icon, title, body, testId }) => (
                    <Link
                        key={title}
                        to="/verification"
                        className="text-center px-4 group transition hover:opacity-90"
                        data-testid={testId}
                    >
                        <div className="mx-auto w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-700 dark:text-emerald-400 group-hover:border-emerald-500/50 transition">
                            <Icon size={18} />
                        </div>
                        <h3 className="mt-3 font-semibold text-base text-foreground group-hover:underline underline-offset-4">{title}</h3>
                        <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                            {body}
                        </p>
                    </Link>
                ))}
            </div>
            <p className="mt-6 text-center text-xs text-muted-foreground">
                <Link to="/verification" className="underline hover:text-foreground" data-testid="verification-strip-learn-more">
                    Read the full verification policy →
                </Link>
            </p>
        </div>
    </section>
);

export default VerificationStrip;
